// BIGTEK Governance Technology Catalogue
// Tiers, in display order
export const TIERS = [
  {
    id: "available-now",
    label: "Available Now",
    short: "Available Now",
    badgeClass: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    dotClass: "bg-emerald-400",
    description:
      "Engagements you can commission today. Fixed-scope, delivery-ready governance services with published indicative pricing.",
  },
  {
    id: "controlled-licences",
    label: "Controlled Licences",
    short: "Controlled Licences",
    badgeClass: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    dotClass: "bg-amber-400",
    description:
      "Frameworks and toolkits supplied under project-specific controlled licences. Deny-by-default, no editable master, no resale.",
  },
  {
    id: "paid-pilots",
    label: "Paid Pilots",
    short: "Paid Pilots",
    badgeClass: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
    dotClass: "bg-cyan-400",
    description:
      "Configurable governance engines delivered as scoped, proposal-based pilots using synthetic, public or expressly authorised data.",
  },
  {
    id: "platform-enterprise",
    label: "Platform & Enterprise",
    short: "Platform & Enterprise",
    badgeClass: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
    dotClass: "bg-indigo-400",
    description:
      "Integrated governance environments. Proof-of-value pilots today; production and enterprise licensing defined separately.",
  },
  {
    id: "roadmap",
    label: "Roadmap",
    short: "Roadmap",
    badgeClass: "bg-slate-500/10 text-slate-300 border-slate-500/30",
    dotClass: "bg-slate-400",
    description:
      "Concepts in active design. No pricing published — register your interest to help shape scope and be notified first.",
  },
];

export const tierById = (id) => TIERS.find((t) => t.id === id);

// Brand imagery supplied by client
export const BRAND_IMAGES = {
  sphere:
    "https://customer-assets-rejwkqb3.emergentagent.net/job_bigtek-products/artifacts/r9z996gn_1000019640%20%281%29.jpg",
  architecture:
    "https://customer-assets-rejwkqb3.emergentagent.net/job_bigtek-products/artifacts/2upv1s91_1000019639%20%281%29.jpg",
  echidna:
    "https://customer-assets-rejwkqb3.emergentagent.net/job_bigtek-products/artifacts/o1rikzfz_unnamed%20%281%29%20%281%29.jpg",
  catalogue:
    "https://customer-assets-rejwkqb3.emergentagent.net/job_bigtek-products/artifacts/bntgy50k_ChatGPT%20Image%20Aug%2016%2C%202026%2C%2010_52_11%20AM.png",
};

const IMAGE_BY_SLUG = {
  "governance-readiness-assessment": BRAND_IMAGES.architecture,
  "indigenous-data-sovereignty-baseline-review": BRAND_IMAGES.sphere,
  "big-kh-001-nations-digital-platform-discovery-toolkit": BRAND_IMAGES.catalogue,
  "big-kh-002-first-nations-governance-architecture-pack": BRAND_IMAGES.architecture,
  "lore-gate-permissions-engine": BRAND_IMAGES.sphere,
  "quill-risk-override-engine": BRAND_IMAGES.architecture,
  "fpic-workflow-system": BRAND_IMAGES.sphere,
  "icip-attestation-workflow": BRAND_IMAGES.catalogue,
  "authority-mapping-engine": BRAND_IMAGES.architecture,
  "cultural-permission-layer": BRAND_IMAGES.sphere,
  "bigibila-governance-engine-mvp": BRAND_IMAGES.catalogue,
  "bigibila-policy-engine": BRAND_IMAGES.architecture,
  "bigibila-knowledge-vault": BRAND_IMAGES.sphere,
  "bigibila-cloud-protected-environment": BRAND_IMAGES.catalogue,
  "managed-nations-digital-platform": BRAND_IMAGES.catalogue,
  "marruma-li-protection-records-repair-pathway": BRAND_IMAGES.echidna,
};

export const productImage = (slug) => IMAGE_BY_SLUG[slug] || BRAND_IMAGES.sphere;

// Products
export const PRODUCTS = [
  {
    slug: "governance-readiness-assessment",
    name: "Governance Readiness Assessment",
    tm: true,
    tier: "available-now",
    tagline:
      "A structured assessment of decision authority, evidence, consent, escalation and implementation readiness.",
    description:
      "A structured assessment of decision authority, evidence, consent, escalation and implementation readiness. It produces an evidence-based picture of where your program stands and what must be resolved before governed delivery can proceed.",
    boundaries: [
      "Reports evidence, gaps and readiness.",
      "It is not cultural certification.",
      "It does not speak on behalf of a Nation or community.",
    ],
    bestFor:
      "Boards, executives, program sponsors, transformation offices and project owners.",
    price: "From $7,500 + GST",
    priceType: "from",
  },
  {
    slug: "indigenous-data-sovereignty-baseline-review",
    name: "Indigenous Data Sovereignty Baseline Review",
    tm: false,
    tier: "available-now",
    tagline:
      "A practical baseline for governing data collection, access, storage, use, sharing, retention and exit.",
    description:
      "A practical baseline for governing data collection, access, storage, use, sharing, retention and exit. It gives institutional teams a defensible starting position for data-governance obligations across the full data lifecycle.",
    boundaries: [
      "Identifies data-governance risks and operating boundaries.",
      "Nation authority and project-specific cultural permissions must still be independently validated.",
      "BIGTEK does not claim universal authority over Indigenous data.",
    ],
    bestFor:
      "Government, enterprise, research, archives, service providers and data-platform teams.",
    price: "From $12,000 + GST",
    priceType: "from",
  },
  {
    slug: "big-kh-001-nations-digital-platform-discovery-toolkit",
    name: "BIG-KH-001 — Nations Digital Platform Discovery Toolkit",
    tm: false,
    tier: "available-now",
    tagline:
      "A fixed-scope discovery engagement that converts a Nation-centred platform concept into a governed product, pilot and procurement brief.",
    description:
      "A fixed-scope discovery engagement that converts a Nation-centred platform concept into a governed product, pilot and procurement brief. It de-risks funding and procurement decisions before larger commitments are made.",
    boundaries: [
      "Uses synthetic and public-data design outputs unless explicit authority permits other knowledge.",
      "Restricted cultural content is not required for discovery.",
    ],
    bestFor:
      "Nations, Indigenous organisations, community-controlled entities, government partners and funders.",
    price: "From $25,000 + GST",
    priceType: "from",
  },
  {
    slug: "big-kh-002-first-nations-governance-architecture-pack",
    name: "BIG-KH-002 — First Nations Governance Architecture Pack",
    tm: true,
    tier: "controlled-licences",
    tagline:
      "A five-layer governance framework and implementation toolkit supplied through a project-specific controlled licence.",
    description:
      "A five-layer governance framework and implementation toolkit supplied through a project-specific controlled licence. It provides traceable governance architecture that procurement and reform teams can implement with confidence.",
    boundaries: [
      "The editable master is not distributed.",
      "Each licence defines the project, authorised users, permitted derivatives, authority pathway, evidence requirements, licence term, withdrawal and exit controls.",
      "No sublicensing, resale, AI training or out-of-scope reuse.",
    ],
    bestFor:
      "Procurement, reform, policy, program and platform teams requiring traceable governance architecture.",
    price:
      "From $20,000 + GST for framework activation; full architecture engagements from $65,000 + GST",
    priceType: "from",
    licence: true,
  },
  {
    slug: "lore-gate-permissions-engine",
    name: "Lore Gate — Permissions Engine",
    tm: true,
    tier: "paid-pilots",
    tagline:
      "A configurable gate workflow for FPIC, ICIP, Lore and cultural-authority clearance before a project progresses.",
    description:
      "A configurable gate workflow for FPIC, ICIP, Lore and cultural-authority clearance before a project progresses. It creates an auditable record of the conditions under which work is permitted to move forward.",
    boundaries: [
      "Records approved authority rules, evidence, conditions, approval, return, halt, expiry and withdrawal.",
      "Administrators cannot invent consent.",
      "Administrators cannot override cultural authority.",
      "Administrators cannot erase withdrawal history.",
    ],
    bestFor:
      "Government, archives, research, cultural collections and platform teams.",
    price: "By proposal",
    priceType: "proposal",
  },
  {
    slug: "quill-risk-override-engine",
    name: "Quill — Risk & Override Engine",
    tm: true,
    tier: "paid-pilots",
    tagline:
      "A risk, escalation and assurance workflow that operationalises Q-1, Q-2 and Q-3 intervention pathways.",
    description:
      "A risk, escalation and assurance workflow that operationalises Q-1, Q-2 and Q-3 intervention pathways. It keeps human decision-makers in control while making escalation and corrective action traceable.",
    boundaries: [
      "Supports human decision-makers through triage, escalation, pause, review and corrective-action workflows.",
      "It does not autonomously determine cultural safety.",
      "It does not present an algorithmic score as cultural authority.",
    ],
    bestFor:
      "Program owners, delivery-assurance teams, transformation offices and governance bodies.",
    price: "By proposal",
    priceType: "proposal",
  },
  {
    slug: "fpic-workflow-system",
    name: "FPIC Workflow System",
    tm: false,
    tier: "paid-pilots",
    tagline:
      "Records affected groups, rightful authority, information supplied, consent stages, conditions, review and withdrawal.",
    description:
      "Records affected groups, rightful authority, information supplied, consent stages, conditions, review and withdrawal. It gives projects a defensible, end-to-end record of a Free, Prior and Informed Consent process.",
    boundaries: [
      "Does not substitute consent.",
      "Does not manufacture authority.",
      "Does not use hidden default approval.",
    ],
    bestFor:
      "Nations, community-controlled organisations, government programs, researchers and project teams requiring traceable FPIC processes.",
    price: "By proposal",
    priceType: "proposal",
  },
  {
    slug: "icip-attestation-workflow",
    name: "ICIP Attestation Workflow",
    tm: false,
    tier: "paid-pilots",
    tagline:
      "Records custodianship, attribution, access, permitted use, restrictions, benefit sharing and takedown requirements.",
    description:
      "Records custodianship, attribution, access, permitted use, restrictions, benefit sharing and takedown requirements. It documents Indigenous Cultural and Intellectual Property obligations across a project's lifecycle.",
    boundaries: [
      "An attestation does not transfer collective cultural rights.",
      "An attestation does not convert community knowledge into a commercial asset.",
    ],
    bestFor:
      "Archives, museums, researchers, cultural collections, government and digital-platform teams.",
    price: "By proposal",
    priceType: "proposal",
  },
  {
    slug: "authority-mapping-engine",
    name: "Authority Mapping Engine",
    tm: false,
    tier: "paid-pilots",
    tagline:
      "Maps roles, delegations, decision rights, escalation routes and evidence of current authority.",
    description:
      "Maps roles, delegations, decision rights, escalation routes and evidence of current authority. It makes complex, multi-party decision-making legible so the right people are making the right decisions with evidence.",
    boundaries: [
      "Reports verified authority and identified gaps.",
      "It cannot create, manufacture or permanently fix cultural authority.",
    ],
    bestFor:
      "Nations, Indigenous organisations, government programs, partnerships and complex multi-party initiatives.",
    price: "By proposal",
    priceType: "proposal",
  },
  {
    slug: "cultural-permission-layer",
    name: "Cultural Permission Layer",
    tm: false,
    tier: "paid-pilots",
    tagline:
      "Applies approved access rules, deny-by-default controls and auditable permission decisions across a test workflow.",
    description:
      "Applies approved access rules, deny-by-default controls and auditable permission decisions across a test workflow. It demonstrates how permissioned access can operate while preserving human review and cultural authority.",
    boundaries: [
      "Must retain human review, non-digital participation, sealed knowledge categories, withdrawal and takedown.",
      "It cannot override cultural authority.",
    ],
    bestFor:
      "Data platforms, archives, knowledge repositories, research environments and government digital programs.",
    price: "By proposal",
    priceType: "proposal",
  },
  {
    slug: "bigibila-governance-engine-mvp",
    name: "BIGIBILA Governance Engine MVP",
    tm: false,
    tier: "platform-enterprise",
    tierNote: "Platform & Enterprise — delivered initially as a Paid Pilot",
    tagline:
      "A secure proof-of-value combining readiness, authority gates, decisions, evidence and reporting for one governed use case.",
    description:
      "A secure proof-of-value combining readiness, authority gates, decisions, evidence and reporting for one governed use case. It lets an organisation test an integrated governance environment before committing to production.",
    boundaries: [
      "Limited to one approved use case, user group and authority model using synthetic, public or expressly authorised pilot data.",
      "It is not production-ready software.",
      "It is not accredited infrastructure.",
      "It is not a universal cultural-governance solution.",
      "It is not a substitute for legal and cultural review.",
    ],
    bestFor:
      "Government, enterprise, Nations, archives and platform teams ready to test an integrated governance environment.",
    price: "From $125,000 + GST",
    priceType: "from",
    enterpriseBoundary:
      "Any future production or enterprise licence must separately define users, data, configuration, security, hosting, audit, support, cultural authority and sovereign-exit requirements. Pilot rights do not imply production rights, perpetual access or ongoing hosting.",
  },
  {
    slug: "bigibila-policy-engine",
    name: "Bigibila Policy Engine",
    tm: true,
    tier: "roadmap",
    tagline: "Decision-support rules tested against authority, rights, evidence and risk.",
    description:
      "Decision-support rules tested against authority, rights, evidence and risk. A concept in active design to support policy and reform teams with traceable, evidence-based decision support.",
    boundaries: [
      "Human approval remains mandatory.",
      "The engine will not autonomously create policy authority.",
      "The engine will not make final cultural decisions.",
    ],
    bestFor: "Policy, reform, assurance and governance teams.",
    price: "Register Interest",
    priceType: "interest",
  },
  {
    slug: "bigibila-knowledge-vault",
    name: "Bigibila Knowledge Vault",
    tm: true,
    tier: "roadmap",
    tagline:
      "A governed repository concept for permissions, provenance, versions, approvals, access, withdrawal and archival controls.",
    description:
      "A governed repository concept for permissions, provenance, versions, approvals, access, withdrawal and archival controls. A concept in active design for custodians who need governed, auditable storage of sensitive knowledge.",
    boundaries: [
      "Storage does not transfer ownership or cultural rights.",
      "Access remains controlled by verified authority, permissions and withdrawal settings.",
    ],
    bestFor:
      "Nations, archives, cultural collections, research institutions and knowledge custodians.",
    price: "Register Interest",
    priceType: "interest",
  },
  {
    slug: "bigibila-cloud-protected-environment",
    name: "Bigibila Cloud — Protected Environment",
    tm: true,
    tier: "roadmap",
    tagline:
      "A proposed protected hosting environment with Nation-specific governance, sovereign exit and controlled operational access.",
    description:
      "A proposed protected hosting environment with Nation-specific governance, sovereign exit and controlled operational access. A concept in active design for programs managing sensitive information under sovereign conditions.",
    boundaries: [
      "It is not currently represented as accredited, production-ready or universally sovereign infrastructure.",
      "Hosting cannot replace Nation authority or project-specific assurance.",
    ],
    bestFor:
      "Nations, community-controlled organisations, government and enterprise programs managing sensitive information.",
    price: "Register Interest",
    priceType: "interest",
  },
  {
    slug: "managed-nations-digital-platform",
    name: "Managed Nations Digital Platform",
    tm: false,
    tier: "roadmap",
    tagline:
      "A future Nation-centred platform service combining portals, permissions, evidence, reporting and governed coordination.",
    description:
      "A future Nation-centred platform service combining portals, permissions, evidence, reporting and governed coordination. A concept in active design, implemented only under Nation-specific authority and arrangements.",
    boundaries: [
      "Each implementation requires Nation-specific authority, governance, permissions and benefit arrangements.",
      "It is not a universal or centrally imposed governance platform.",
    ],
    bestFor:
      "Nations and community-controlled organisations seeking governed digital infrastructure.",
    price: "Register Interest",
    priceType: "interest",
  },
  {
    slug: "marruma-li-protection-records-repair-pathway",
    name: "Marruma-li — Protection & Records Repair Pathway",
    tm: true,
    tier: "roadmap",
    tagline:
      "A proposed workflow supporting documentation, notice, correction, restriction, restoration and review of affected records.",
    description:
      "A proposed workflow supporting documentation, notice, correction, restriction, restoration and review of affected records. A concept in active design for agencies and communities addressing harmful or inaccurate records.",
    boundaries: [
      "Supports accountable records-repair processes.",
      "Does not automatically determine truth, cultural authority, legal liability or entitlement.",
    ],
    bestFor:
      "Government, justice, archives, records agencies and community-controlled organisations addressing harmful or inaccurate records.",
    price: "Register Interest",
    priceType: "interest",
  },
];

export const productBySlug = (slug) => PRODUCTS.find((p) => p.slug === slug);
export const productsByTier = (tierId) => PRODUCTS.filter((p) => p.tier === tierId);

export const ORG_TYPES = [
  "Government agency",
  "Enterprise / transformation office",
  "Procurement team",
  "Nation",
  "Indigenous organisation",
  "Community-controlled entity",
  "Research body",
  "Archive / cultural collection",
  "Other",
];

export const TIMELINES = [
  "Immediate (0–3 months)",
  "Near term (3–6 months)",
  "This financial year",
  "Exploratory / no fixed date",
];

// Governance Readiness Self-Check questions
export const SELFCHECK_QUESTIONS = [
  {
    id: "decision_authority",
    dimension: "Decision Authority",
    question:
      "Is it clearly documented who holds the authority to make key decisions on this program?",
  },
  {
    id: "cultural_authority",
    dimension: "Cultural Authority",
    question:
      "Where cultural knowledge or Country is involved, has the rightful authority been identified and engaged?",
  },
  {
    id: "consent",
    dimension: "Consent (FPIC)",
    question:
      "Do you have a documented process for Free, Prior and Informed Consent before work proceeds?",
  },
  {
    id: "evidence",
    dimension: "Evidence",
    question:
      "Can you produce evidence to support the authority and consent decisions you have made?",
  },
  {
    id: "data_lifecycle",
    dimension: "Data Lifecycle",
    question:
      "Are collection, access, storage, use, sharing, retention and exit rules defined for your data?",
  },
  {
    id: "icip",
    dimension: "ICIP",
    question:
      "Are Indigenous Cultural and Intellectual Property attribution and restrictions recorded and honoured?",
  },
  {
    id: "escalation",
    dimension: "Escalation",
    question:
      "Is there a defined escalation and intervention pathway when risks or objections arise?",
  },
  {
    id: "withdrawal",
    dimension: "Withdrawal & Takedown",
    question:
      "Can a community or authority withdraw consent or request takedown, with that history preserved?",
  },
  {
    id: "deny_by_default",
    dimension: "Access Controls",
    question:
      "Does access to sensitive information operate on a deny-by-default basis with human review?",
  },
  {
    id: "sovereign_exit",
    dimension: "Sovereign Exit",
    question:
      "If the engagement ended, could data and control be returned cleanly to its rightful custodians?",
  },
  {
    id: "human_in_loop",
    dimension: "Human Oversight",
    question:
      "Do humans retain final decision-making, rather than an algorithm or score acting as authority?",
  },
  {
    id: "implementation",
    dimension: "Implementation Readiness",
    question:
      "Is your team resourced and ready to implement governed processes, not just document them?",
  },
];

export const SELFCHECK_OPTIONS = [
  { value: 2, label: "Yes — documented and in practice" },
  { value: 1, label: "Partially — in progress or informal" },
  { value: 0, label: "No — not yet addressed" },
  { value: 0, label: "Unsure" },
];
